import type { CustomerIdentifiers } from "@redotech/redo-model/customer";
import type { WidgetEventPayload } from "@redotech/redo-model/customer-event/widget-event-payloads";

/**
 * Redo marketing browser SDK.
 *
 * A small, auth-agnostic client for emitting Redo customer events from the
 * browser. Today it backs Redo's own frontends; the same `identify`/`track`
 * surface is intended to graduate into a partner-facing SDK without changing
 * callers — the only thing that swaps is the injected {@link EventTransport}
 * (internal authenticated widget client now, public-key transport later).
 */

/**
 * Customer identity attached to events. Mirrors the pipeline's
 * `CustomerIdentifiers`, so any identifier the pipeline understands (email,
 * phoneNumber, anonymousId, shopifyCustomerId, …) can be set without changing
 * the envelope.
 */
export type EventIdentifiers = CustomerIdentifiers;

/** The wire envelope sent to the widget events intake. */
export interface WidgetEventEnvelope {
  event: string;
  /** Customer identity (identifiers) attached to the event. */
  customer?: EventIdentifiers;
  /** Idempotency key for the event (caller-supplied or SDK-generated uuid). */
  eventId: string;
  payload: unknown;
}

/**
 * Pluggable delivery. Internal apps inject a transport built on their
 * authenticated widget client; a partner build injects a public-key transport.
 * The SDK never owns auth or routing, so it stays publishable as-is.
 */
export type EventTransport = (
  envelope: WidgetEventEnvelope,
) => Promise<void> | void;

export interface RedoMarketingClientOptions {
  transport: EventTransport;
}

export interface RedoMarketingClient {
  /** Set the identifiers attached to subsequent track calls. Merges. */
  identify(identifiers: EventIdentifiers): void;
  /**
   * Fire a custom event — the conventional analytics-SDK `track(name, …)`
   * shape: an arbitrary event name plus optional free-form `properties` and
   * first-class commerce context (`value`, `valueCurrency`, `lineItems`). Maps
   * to CUSTOM_EVENT. Pass `eventId` to control dedup; otherwise one is
   * generated. For events we have schemas for, use the typed methods below.
   */
  track(
    eventName: string,
    args?: Omit<WidgetEventPayload<"custom">, "eventName"> & {
      eventId?: string;
    },
  ): Promise<void>;
  /**
   * Typed convenience methods for the events we have schemas for. The payload
   * is checked against the event's schema; the SDK generates the eventId. Add
   * one per new schema'd event.
   */
  trackingPageView(
    payload: WidgetEventPayload<"tracking_page_viewed">,
  ): Promise<void>;
  trackingPageClick(
    payload: WidgetEventPayload<"tracking_page_clicked">,
  ): Promise<void>;
}

export function createRedoMarketingClient(
  options: RedoMarketingClientOptions,
): RedoMarketingClient {
  let customer: EventIdentifiers = {};

  // Internal primitive: post one event envelope through the transport. All
  // public methods funnel through this. Generates an eventId when the caller
  // doesn't supply one.
  const emit = async (
    event: string,
    payload: unknown,
    eventId?: string,
  ): Promise<void> => {
    await options.transport({
      event,
      customer,
      eventId: eventId ?? crypto.randomUUID(),
      payload,
    });
  };

  return {
    identify(identifiers) {
      customer = { ...customer, ...identifiers };
    },
    track(eventName, args) {
      const { eventId, ...rest } = args ?? {};
      return emit("custom", { eventName, ...rest }, eventId);
    },
    trackingPageView(payload) {
      return emit("tracking_page_viewed", payload);
    },
    trackingPageClick(payload) {
      return emit("tracking_page_clicked", payload);
    },
  };
}
